import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import db from '../firebase-config.ts'; // Import Firestore configuration
import { collection, getDocs } from 'firebase/firestore';
import { useUser } from './UserProvider.tsx'; // Import the custom hook

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

const StudentCategoryDashboard: React.FC = () => {
  const { isAuthenticated } = useUser(); // Access user authentication state
  const navigate = useNavigate(); // Initialize navigation

  const [categorySummary, setCategorySummary] = useState({
    totalStudents: 0,
    below1000: 0,
    B40: 0,
    M20: 0,
    T20: 0,
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login'); // Redirect to login page
    }
  }, [isAuthenticated, navigate]);

  // Fetch data from Firestore
  useEffect(() => {
    const fetchData = async () => {
      const studentsCollection = collection(db, 'students');
      const snapshot = await getDocs(studentsCollection);
      const students = snapshot.docs.map((doc) => doc.data());

      console.log('Raw Data:', students);

      // Categorize students based on purata_pendapatan
      const below1000 = students.filter(
        (student) =>
          student.purata_pendapatan &&
          student.purata_pendapatan.includes('<RM1000')
      ).length;

      const B40 = students.filter(
        (student) =>
          student.purata_pendapatan &&
          (student.purata_pendapatan.includes('<RM4849') ||
            student.purata_pendapatan.includes('<RM2000') ||
            student.purata_pendapatan.includes('<RM3000'))
      ).length;

      const M20 = students.filter(
        (student) =>
          student.purata_pendapatan &&
          student.purata_pendapatan.includes('>RM4850') &&
          !student.purata_pendapatan.includes('>RM10960')
      ).length;

      const T20 = students.filter(
        (student) =>
          student.purata_pendapatan &&
          student.purata_pendapatan.includes('>RM10960')
      ).length;

      const totalStudents = students.length;

      setCategorySummary({ totalStudents, below1000, B40, M20, T20 });
    };

    fetchData();
  }, []);

  const pieData = {
    labels: ['<RM1000', 'B40 (<=RM4850)', 'M20 (>RM4850 - <=RM10960)', 'T20 (>RM10960)'],
    datasets: [
      {
        data: [
          categorySummary.below1000,
          categorySummary.B40,
          categorySummary.M20,
          categorySummary.T20,
        ],
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
        hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Student Category Distribution (B40, M20, T20)',
      },
    },
  };

  return isAuthenticated ? (
    <div className="min-h-screen p-6">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-white">WELCOME TO SMART IKAS</h1>
        <p className="text-white">Powered by Azmi Productions</p>
      </header>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-bold">Total Students</h2>
          <p className="text-2xl">{categorySummary.totalStudents}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-bold">B40 Students</h2>
          <p className="text-2xl">
            {categorySummary.B40} (
            {((categorySummary.B40 / categorySummary.totalStudents) * 100).toFixed(2)}%)
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-bold">M20 Students</h2>
          <p className="text-2xl">
            {categorySummary.M20} (
            {((categorySummary.M20 / categorySummary.totalStudents) * 100).toFixed(2)}%)
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-bold">T20 Students</h2>
          <p className="text-2xl">
            {categorySummary.T20} (
            {((categorySummary.T20 / categorySummary.totalStudents) * 100).toFixed(2)}%)
          </p>
        </div>
      </div>

      {/* Pie Chart for Distribution */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="text-lg font-bold mb-4">Student Category Distribution</h2>
        <div className="flex justify-center items-center">
          <div className="w-96 h-96">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>
    </div>
  ) : null; // Prevent unnecessary rendering if not authenticated
};

export default StudentCategoryDashboard;
